import { DefaultError, MutationFunction } from '@tanstack/react-query'
import { UseApiMutationProps, useApiMutation } from './use-api-mutation'
import {
  UseApiWrapperWithValidationProps,
  useApiWrapperWithValidation,
} from './use-api-wrapper-with-validation'

interface UseApiMutationWithValidationProps<
  TData = unknown,
  TError = DefaultError,
  TVariables = unknown,
  TContext = unknown,
  SetQueryDataData = unknown,
> extends UseApiMutationProps<
      TData,
      TError,
      TVariables,
      TContext,
      SetQueryDataData
    >,
    UseApiWrapperWithValidationProps<TData, TVariables[]> {}

export const useMutationWithValidation = <
  TData = unknown,
  TError = DefaultError,
  TVariables = unknown,
  TContext = unknown,
  SetQueryDataData = unknown,
>({
  requestFunction,
  handleSuccess,
  handleError,
  handleFinally,
  successToastMessage,
  ...restProps
}: UseApiMutationWithValidationProps<
  TData,
  TError,
  TVariables,
  TContext,
  SetQueryDataData
>) => {
  const { fetcher } = useApiWrapperWithValidation<TData, TVariables[], true>({
    requestFunction,
    successToastMessage,
    handleSuccess,
    handleError,
    handleFinally,
    doThrowError: true,
  })

  const { mutate, isPending, ...rest } = useApiMutation<
    TData,
    TError,
    TVariables,
    TContext,
    SetQueryDataData
  >({
    mutationFn: fetcher as MutationFunction<TData, TVariables>,
    ...restProps,
  })

  return {
    fetcher: mutate,
    isLoading: isPending,
    ...rest,
  }
}
